import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { Box, Button, Column, FullPageModal, FullPageModalHeaderStart, Row, Typography, useDevice, useOverlayContext } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { SignSlider } from '~/client-flow/clientOb/components/MobileSign/SignSlider';
import BankSecurityRow from '~/components/BankSecurityRow/BankSecurityRow';
import { InvoiceHeading } from '~/components/InvoiceHeading/InvoiceHeading';
import ManagePaymentMethods from '~/components/ManagePaymentMethods/ManagePaymentMethods';
import { useRelationship } from '~/hooks/reactQuery/queries/useRelationship';
import { useLocaleKeys } from '~/i18n';
import { MoneyModel, invoiceModel, relationshipModel, timeModel } from '~/models';
import StripeElementsProvider from '~/providers/StripeElementsProvider';
import styles from './AdHocInvoiceApproval.module.scss';
import { applyApprovalError, formatVendorAddress } from './AdHocInvoiceApproval.utils';
import { AdHocStatusView } from './AdHocStatusView/AdHocStatusView';
import { deriveDisplayState } from './AdHocStatusView/AdHocStatusView.utils';
import { useAdHocInvoice } from './hooks/useAdHocInvoice';
import { useApproveAdHocInvoice } from './hooks/useApproveAdHocInvoice';
import { useRetryAdHocPayment } from './hooks/useRetryAdHocPayment';
import { useViewInvoicePdf } from './hooks/useViewInvoicePdf';
import { InvoiceSummaryPanel } from './InvoiceSummaryPanel/InvoiceSummaryPanel';
import { TermsOfService } from './TermsOfService/TermsOfService';
export const GetAdHocInvoiceApprovalScreenTID = (parentTestId)=>getTestIds(parentTestId);
export const AdHocInvoiceApprovalScreen = (props)=>{
    const { invoiceId, vendorName: fallbackVendorName, hideViewInvoice, chainedApproval, onClose, registerCanDismiss, onApproved, testId } = props;
    const { AdHocInvoiceApprovalTestIds } = useMemo(()=>GetAdHocInvoiceApprovalScreenTID(testId), [
        testId
    ]);
    const { t } = useLocaleKeys();
    const { isMobileDevice } = useDevice();
    const overlayContext = useOverlayContext();
    const [state, setState] = useState({
        kind: 'ready'
    });
    const [selectedFundingSourceId, setSelectedFundingSourceId] = useState(null);
    const [tosChecked, setTosChecked] = useState(false);
    const [mobileStep, setMobileStep] = useState('summary');
    const [sliderPercent, setSliderPercent] = useState(0);
    const [sliderKey, setSliderKey] = useState(0);
    const [showFullPageInvoice, setShowFullPageInvoice] = useState(false);
    useEffect(()=>{
        if (isMobileDevice && onClose == null) {
            document.body.style.setProperty('--bg-standard', '#ffffff');
            return ()=>{
                document.body.style.removeProperty('--bg-standard');
            };
        }
    }, [
        isMobileDevice,
        onClose
    ]);
    const { approveAdHocInvoice, isLoading: isApproving } = useApproveAdHocInvoice();
    const { retryAdHocPayment, isPending: isRetrying } = useRetryAdHocPayment({
        errorTestId: AdHocInvoiceApprovalTestIds.ApproveError()
    });
    const { invoice: scoutInvoice, isLoading: isInvoiceLoading, refresh: refreshInvoice } = useAdHocInvoice(invoiceId);
    const amount = scoutInvoice != null ? new MoneyModel(invoiceModel.rawTotalAmount(scoutInvoice)) : MoneyModel.fromDTO({
        amount: 0
    });
    const invoiceNumber = scoutInvoice != null ? invoiceModel.formattedNumber(scoutInvoice) : '';
    const { viewInvoicePdf } = useViewInvoicePdf({
        invoiceId,
        invoiceNumber
    });
    const relationshipId = scoutInvoice != null ? invoiceModel.relationshipId(scoutInvoice) : '';
    const { relationship, isLoading: isRelationshipLoading } = useRelationship(relationshipId, {
        resolvePaymentMethod: false
    });
    const legalTermsDocument = scoutInvoice != null ? scoutInvoice.legalTermsDocument : null;
    const buyerAlreadyAcceptedTos = scoutInvoice != null && invoiceModel.isPaymentFailed(scoutInvoice);
    const requiresTos = legalTermsDocument != null && !buyerAlreadyAcceptedTos;
    const vendorId = relationship != null ? relationshipModel.vendor(relationship) : '';
    const customerId = relationship != null ? relationshipModel.customer(relationship) : '';
    const hasPaymentMethodIds = vendorId !== '' && customerId !== '';
    const isApproveDisabled = selectedFundingSourceId == null || requiresTos && !tosChecked;
    const vendorLogoUrl = relationship != null ? relationshipModel.vendorLogoUrl(relationship) : undefined;
    const vendorAddress = relationship != null ? formatVendorAddress(relationship.vendor.postalAddress) : undefined;
    const customerName = scoutInvoice != null ? invoiceModel.customerName(scoutInvoice) : undefined;
    const vendorName = scoutInvoice != null ? invoiceModel.vendorName(scoutInvoice) : fallbackVendorName;
    const issueDate = scoutInvoice != null ? timeModel.formatDateLong(scoutInvoice.rawIssuedOn) : undefined;
    const dueDate = scoutInvoice != null ? timeModel.formatDateLong(scoutInvoice.rawDueDate) : undefined;
    const toConfirmation = ()=>{
        setState({
            kind: 'confirmation',
            amount,
            vendorName
        });
        onApproved === null || onApproved === void 0 ? void 0 : onApproved();
    };
    const resetSlider = ()=>{
        setSliderKey((k)=>k + 1);
        setSliderPercent(0);
    };
    const showApproveError = ()=>{
        resetSlider();
        overlayContext.showErrorToast(t.invoice.adHocApproval.approveCta.approveError(), AdHocInvoiceApprovalTestIds.ApproveError());
    };
    const markVoided = ()=>setState({
            kind: 'voided',
            vendorName,
            amount
        });
    const handleApproveError = (error)=>applyApprovalError({
            error,
            onAlreadyApproved: toConfirmation,
            onVoided: markVoided,
            onUnclassified: showApproveError
        });
    const submitApproval = (fundingSourceId)=>buyerAlreadyAcceptedTos ? retryAdHocPayment({
            invoiceId,
            fundingSourceId
        }, {
            onSuccess: toConfirmation,
            onError: resetSlider
        }) : approveAdHocInvoice({
            invoiceId,
            fundingSourceId,
            approvedLegalTermsId: legalTermsDocument === null || legalTermsDocument === void 0 ? void 0 : legalTermsDocument.legalTermsId
        }, {
            onSuccess: toConfirmation,
            onError: handleApproveError
        });
    const handleApprove = ()=>{
        if (selectedFundingSourceId != null) submitApproval(selectedFundingSourceId);
    };
    const approveCta = isMobileDevice ? /*#__PURE__*/ _jsxs(Column, {
        gap: 8,
        children: [
            /*#__PURE__*/ _jsxs(Column, {
                testId: AdHocInvoiceApprovalTestIds.ApproveCta(),
                className: styles.sliderWrapper,
                children: [
                    /*#__PURE__*/ _jsx(SignSlider, {
                        disabled: isApproveDisabled || isApproving || isRetrying,
                        onSign: handleApprove,
                        onChange: setSliderPercent
                    }, sliderKey),
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph1",
                        weight: "normal",
                        className: styles.sliderHint,
                        style: {
                            opacity: Math.max(1 - 6 * sliderPercent, 0)
                        },
                        children: t.invoice.adHocApproval.approveCta.approveAndPay({
                            amount: amount.display()
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Typography, {
                variant: "paragraph3",
                weight: "normal",
                className: styles.disclaimer,
                children: t.invoice.adHocApproval.approveCta.disclaimer()
            }),
            /*#__PURE__*/ _jsx(BankSecurityRow, {
                className: styles.bankSecurity
            })
        ]
    }) : /*#__PURE__*/ _jsxs(Column, {
        gap: 8,
        children: [
            /*#__PURE__*/ _jsx(Button, {
                bi: {
                    name: 'approve_ad_hoc_invoice'
                },
                testId: AdHocInvoiceApprovalTestIds.ApproveCta(),
                skin: "primary",
                size: "large",
                fluid: true,
                disabled: isApproveDisabled,
                loading: isApproving || isRetrying,
                onClick: handleApprove,
                children: t.invoice.adHocApproval.approveCta.approveAndPay({
                    amount: amount.display()
                })
            }),
            /*#__PURE__*/ _jsx(Typography, {
                variant: "paragraph3",
                weight: "normal",
                className: styles.disclaimer,
                children: t.invoice.adHocApproval.approveCta.disclaimer()
            }),
            /*#__PURE__*/ _jsx(BankSecurityRow, {
                className: styles.bankSecurity
            })
        ]
    });
    const paymentMethodOptions = /*#__PURE__*/ _jsxs(Column, {
        className: styles.rightPanelTop,
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                testId: AdHocInvoiceApprovalTestIds.PaymentMethodPanel(),
                variant: "paragraph1",
                weight: "bold",
                className: styles.rightPanelTitle,
                children: t.invoice.adHocApproval.paymentMethodPanel.title()
            }),
            /*#__PURE__*/ _jsx(Column, {
                className: styles.paymentCard,
                children: hasPaymentMethodIds ? /*#__PURE__*/ _jsx(StripeElementsProvider, {
                    children: /*#__PURE__*/ _jsx(ManagePaymentMethods, {
                        isProposal: true,
                        isAdHoc: true,
                        updateProgress: false,
                        vendorId: vendorId,
                        customerId: customerId,
                        clientName: relationship != null ? relationshipModel.customerName(relationship) : undefined,
                        relationshipId: relationshipId,
                        isPreview: false,
                        isSample: false,
                        isInviteMemberDisabled: true,
                        hideInviteMemberHint: true,
                        showCreditCardFee: relationship != null ? !relationshipModel.actualIsVendorCoveringCCFee(relationship) : true,
                        isVendorCoveringFees: relationship != null ? relationshipModel.actualIsVendorCoveringCCFee(relationship) : false,
                        onSelectPaymentMethod: (pm)=>{
                            var _ref;
                            return setSelectedFundingSourceId((_ref = pm === null || pm === void 0 ? void 0 : pm.id) !== null && _ref !== void 0 ? _ref : null);
                        }
                    })
                }) : null
            })
        ]
    });
    const actionFooterContent = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(TermsOfService, {
                testId: AdHocInvoiceApprovalTestIds.TermsOfService(),
                legalTermsDocument: requiresTos ? legalTermsDocument : null,
                checked: tosChecked,
                onCheckedChange: setTosChecked,
                vendorId: vendorId,
                invoiceId: invoiceId
            }),
            approveCta
        ]
    });
    registerCanDismiss === null || registerCanDismiss === void 0 ? void 0 : registerCanDismiss(()=>Promise.resolve(!isApproving && !isRetrying));
    const showsApproveForm = deriveDisplayState({
        state,
        amount,
        vendorName: fallbackVendorName,
        scoutInvoice,
        relationship,
        isLoading: isInvoiceLoading || isRelationshipLoading,
        showFullPageInvoice
    }).kind === 'approveForm';
    const showsModalBackButton = isMobileDevice && mobileStep === 'payment' && showsApproveForm;
    const modalHeader = /*#__PURE__*/ _jsx(FullPageModalHeaderStart, {
        children: isMobileDevice ? /*#__PURE__*/ _jsx(Row, {
            className: styles.modalHeader,
            children: vendorLogoUrl != null ? /*#__PURE__*/ _jsx("img", {
                src: vendorLogoUrl,
                alt: vendorName,
                className: styles.modalHeaderLogo
            }) : /*#__PURE__*/ _jsx(Typography, {
                variant: "paragraph1",
                weight: "bold",
                className: styles.modalHeaderName,
                children: vendorName
            })
        }) : scoutInvoice != null && /*#__PURE__*/ _jsx(InvoiceHeading, {
            invoice: scoutInvoice
        })
    });
    const summaryProps = {
        testId: AdHocInvoiceApprovalTestIds.InvoiceSummaryPanel(),
        vendorName,
        vendorLogoUrl: vendorLogoUrl !== null && vendorLogoUrl !== void 0 ? vendorLogoUrl : undefined,
        vendorAddress,
        clientName: customerName,
        invoiceNumber,
        issueDate,
        dueDate,
        amountDue: amount,
        chainedApproval,
        onViewInvoiceClick: ()=>viewInvoicePdf({
                showDownloadButton: true
            })
    };
    const invoiceSummary = (mobile)=>/*#__PURE__*/ _jsx(InvoiceSummaryPanel, _object_spread_props(_object_spread({}, summaryProps), {
            hideVendorBlock: mobile || undefined,
            mobile: mobile || undefined
        }));
    const desktopForm = /*#__PURE__*/ _jsx(Box, {
        testId: AdHocInvoiceApprovalTestIds.Root(),
        className: cn(styles.pageWrapper),
        children: /*#__PURE__*/ _jsxs(Box, {
            testId: AdHocInvoiceApprovalTestIds.SplitLayout(),
            className: styles.page,
            children: [
                /*#__PURE__*/ _jsx(Column, {
                    className: styles.leftPanel,
                    children: invoiceSummary(false)
                }),
                /*#__PURE__*/ _jsxs(Column, {
                    className: styles.rightPanel,
                    children: [
                        paymentMethodOptions,
                        /*#__PURE__*/ _jsx(Column, {
                            testId: AdHocInvoiceApprovalTestIds.ActionFooter(),
                            className: styles.rightPanelBottom,
                            children: actionFooterContent
                        })
                    ]
                })
            ]
        })
    });
    const mobileSummaryStep = /*#__PURE__*/ _jsx(Box, {
        testId: AdHocInvoiceApprovalTestIds.Root(),
        className: cn(styles.pageWrapper, styles.pageWrapperMobile),
        children: /*#__PURE__*/ _jsxs(Box, {
            className: cn(styles.page, styles.pageMobile),
            children: [
                /*#__PURE__*/ _jsx(Column, {
                    className: styles.leftPanelMobile,
                    children: invoiceSummary(true)
                }),
                /*#__PURE__*/ _jsx(Column, {
                    className: styles.mobilePayNowSticky,
                    children: /*#__PURE__*/ _jsx(Button, {
                        bi: {
                            name: 'ad_hoc_invoice_mobile_pay_now'
                        },
                        testId: AdHocInvoiceApprovalTestIds.MobilePayNowSticky(),
                        skin: "primary",
                        size: "large",
                        fluid: true,
                        onClick: ()=>setMobileStep('payment'),
                        children: t.invoice.adHocApproval.mobile.payNow()
                    })
                })
            ]
        })
    });
    const mobilePaymentStep = /*#__PURE__*/ _jsx(Box, {
        testId: AdHocInvoiceApprovalTestIds.Root(),
        className: cn(styles.pageWrapper, styles.pageWrapperMobile),
        children: /*#__PURE__*/ _jsxs(Box, {
            className: cn(styles.page, styles.pageMobile),
            children: [
                /*#__PURE__*/ _jsx(Column, {
                    className: cn(styles.rightPanel, styles.rightPanelMobile),
                    children: paymentMethodOptions
                }),
                /*#__PURE__*/ _jsx(Column, {
                    testId: AdHocInvoiceApprovalTestIds.ActionFooter(),
                    className: cn(styles.rightPanelBottom, styles.rightPanelBottomMobile),
                    children: actionFooterContent
                })
            ]
        })
    });
    const renderApproveForm = ()=>{
        if (!isMobileDevice) return desktopForm;
        return mobileStep === 'summary' ? mobileSummaryStep : mobilePaymentStep;
    };
    const handleVerificationResolved = ()=>{
        toConfirmation();
        refreshInvoice();
    };
    const screen = /*#__PURE__*/ _jsx(AdHocStatusView, {
        testId: AdHocInvoiceApprovalTestIds.AdHocStatusView(),
        state: state,
        invoiceId: invoiceId,
        amount: amount,
        vendorName: fallbackVendorName,
        hideViewInvoice: hideViewInvoice,
        invoice: scoutInvoice,
        relationship: relationship,
        isLoading: isInvoiceLoading || isRelationshipLoading,
        showFullPageInvoice: showFullPageInvoice,
        onDismissFullPageInvoice: ()=>setShowFullPageInvoice(false),
        onShowFullPageInvoice: ()=>setShowFullPageInvoice(true),
        onResolved: handleVerificationResolved,
        renderApproveForm: renderApproveForm
    });
    return /*#__PURE__*/ _jsxs(FullPageModal, {
        testId: AdHocInvoiceApprovalTestIds.ModalRoot(),
        hideHeader: showFullPageInvoice || !isMobileDevice && onClose == null,
        hideCloseBtn: onClose == null && !showsModalBackButton,
        usePrevIcon: true,
        closeBiName: showsModalBackButton ? 'ad_hoc_invoice_mobile_back' : undefined,
        onClose: showsModalBackButton ? ()=>setMobileStep('summary') : onClose,
        children: [
            modalHeader,
            screen
        ]
    });
};
